import { OnDestroy } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { CallCenter } from '@app/core/interfaces/c5/acd/call-center';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { C5Enterprise } from '@app/core/interfaces/c5/c5-enterprise';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { PhoneNumberType } from '@app/core/interfaces/c5/phone-number-type';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { Enterprise } from '@app/core/interfaces/enterprise';
import { IvrAssistant } from '@app/core/interfaces/ivr-assistant';
import { ServiceUser } from '@app/core/interfaces/service-user';
import { Domain } from '@app/core/models/domain';
import { DomainType } from '@app/core/models/domain-type';
import { PhoneNumberLike } from '@app/shared/models/phone-number-like';
import { TypeaheadBasketShowServicesEnum } from '@app/shared/models/typeahead-basket-show-services.enum';
import { ContactNamePipe } from '@app/shared/pipes/contact-name.pipe';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { loggerFor } from '@app/utils/logger';
import { sortBy } from '@app/utils/sort/sort-by';
import { StringMap } from '@app/utils/string-map';
import { FeatureStatus } from '@business/feature/interfaces/feature-status';
import { Features } from '@business/feature/interfaces/features';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

@UntilDestroy()
export class TypeaheadBasketService implements OnDestroy {
  private _all: TypeaheadEntry<any, TargetMetaData>[] = [];
  private _phoneBookEntries: PhoneBookContact[] = [];

  public announcements: TypeaheadEntry<any, TargetMetaData>[] = [];
  public custom: StringMap<TypeaheadEntry<any, TargetMetaData>[]> = {};
  public personal: TypeaheadEntry<any, TargetMetaData>[] = [];
  public phoneBook: TypeaheadEntry<any, TargetMetaData>[] = [];
  public services: TypeaheadEntry<any, TargetMetaData>[] = [];

  /** needed for parallel ringing target (currently only mobile from "personal" is in there) */
  public parallelRingingSpecific: TypeaheadEntry<any, TargetMetaData>[] = [];

  get all() {
    return this._all;
  }

  private currentSiteId?: number;
  private customOrder: string[] | null = null;
  private logger = loggerFor('TypeaheadBasketService');

  ngOnDestroy() {}

  /**
   * Translations that are used by the typeahead options (typeaheadBasket)
   */
  private translations: TranslatedLabels;

  private translations$: Observable<StringMap<string>>;

  constructor(
    private authenticationService: AuthenticationService,
    private contactNamePipe: ContactNamePipe,
    private endUserContextService: EndUserContextService,
    private phoneNumberPipe: PhoneNumberPipe,
    private translateService: TranslateService
  ) {
    this.fetchTranslations();

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      // refs #60797: Check if the user is authenticated because it can happen
      // that the language changes upon logout of a user (e.g. because the
      // portal switches to german by default if the user is not authenticated)
      // and in this case we do not want to reload anything. Normally this
      // should be taken care of by the untilDestroyed pipe, but in this case it
      // happens because the component is destroyed when the router navigates to
      // the login page but the language changes before that happens and
      // therefore the subscription is still called.
      if (authenticationService.isAuthenticated) {
        this._refreshOnLangChange();
      }
    });

    if (this.endUserContextService.isActive) {
      this.endUserContextService.endUser.data$.pipe(untilDestroyed(this)).subscribe((endUser) => {
        this.currentSiteId = endUser.user?.domainId;
        // note: this part of the code might be called after the `refresh()` was called already from outside

        this.refreshPhoneBook(this._phoneBookEntries);
        this.refreshAll();
      });
    }
  }

  clear() {
    this._all = [];
    this.announcements = [];
    this.custom = {};
    this.personal = [];
    this.phoneBook = [];
    this.parallelRingingSpecific = [];
  }

  clearServices() {
    this.services = [];

    return this;
  }

  /**
   * Fetch the translations that are needed to display the phonebook entries with correct labels
   */
  fetchTranslations(): void {
    // Stream so we get the new labels every time the language changes
    this.translations$ = this.translateService.stream([
      'announcements',
      'features.target.services',
      'features.target.prefix.acd',
      'features.target.prefix.contact',
      'features.target.prefix.cc',
      'features.target.prefix.endUser',
      'features.target.prefix.ivr',
      'features.target.prefix.serviceUser',
      'features.target.richPresence',
      'features.target.richPresenceAndVoiceMail',
      'mobile',
      'phoneBook.fax',
      'phoneBook.home',
      'phoneBook.mobile',
      'phoneBook.title',
      'phoneBook.work',
      'site.fallbackNumberNotReachable',
      'VOICE_MAIL',
      'common.external',
      'common.internal',
      'common.extension',
      'features.announcement',
    ]);

    this.translations$.subscribe((values: StringMap<string>) => {
      this.translations = {
        announcement: values['features.announcement'],
        announcements: values['announcements'],
        businessFax: values['phoneBook.fax'],
        businessPhone2: values['phoneBook.work'],
        businessPhone: values['phoneBook.work'],
        contact: values['features.target.prefix.contact'],
        endUser: values['features.target.prefix.endUser'],
        external: values['common.external'],
        extension: values['common.extension'],
        fallback: values['site.fallbackNumberNotReachable'],
        fax: values['phoneBook.fax'],
        homeFax: values['phoneBook.homeFax'],
        homePhone2: values['phoneBook.home'],
        homePhone: values['phoneBook.home'],
        internal: values['common.internal'],
        mobile: values['mobile'],
        mobilePhone2: values['phoneBook.mobile'],
        mobilePhone: values['phoneBook.mobile'],
        phoneBook: values['phoneBook.title'],
        prefixAcd: values['features.target.prefix.acd'],
        prefixCc: values['features.target.prefix.cc'],
        prefixIvr: values['features.target.prefix.ivr'],
        prefixServiceUser: values['features.target.prefix.serviceUser'],
        richPresence: values['features.target.richPresence'],
        richPresenceAndVoiceMail: values['features.target.richPresenceAndVoiceMail'],
        services: values['features.target.services'],
        voicemail: values['VOICE_MAIL'],
      };
    });
  }

  public mapPhoneBook(phoneBook: PhoneBookContact): TypeaheadEntry<any, TargetMetaData>[] {
    const entries = [];

    const map = (
      phoneBook: PhoneBookContact,
      value: string,
      type: string
    ): TypeaheadEntry<any, TargetMetaData> => {
      const group = this.translations.phoneBook;
      const name = this.contactNamePipe.transform(phoneBook);

      const namePrefix = phoneBook.userId != null ? this.translations.endUser : this.translations.contact;

      return {
        key: `${name}: ${value}`.toLowerCase(),
        group,
        label: value,
        meta: {
          description: value,
          group: 'phoneBook',
          name: `${namePrefix}${name}`,
          optionName: name,
          type,
        },
        value,
      };
    };

    // Possible numbers of a phonebook entry
    const values = [
      'businessFax',
      'businessPhone2',
      'businessPhone',
      'fax',
      'homeFax',
      'homePhone2',
      'homePhone',
      'mobilePhone2',
      'mobilePhone',
    ];

    // Map all available phone numbers to a KeyValue object that will be displayed in the ng-select
    for (let value of values) {
      if (phoneBook[value] != undefined) {
        // it might be that the shared context end-user is not already loaded, hence guard for undefined
        if (
          value === 'businessPhone' &&
          this.currentSiteId === phoneBook.siteId &&
          phoneBook.extension != undefined
        ) {
          entries.push(map(phoneBook, phoneBook.extension, this.translations[value]));
        } else {
          entries.push(map(phoneBook, phoneBook[value], this.translations[value]));
        }
      }
    }

    return entries;
  }

  public setCustomOrder(customOrder: string[] | null) {
    this.customOrder = customOrder;

    return this;
  }

  public setSiteId(id: number | null): void {
    this.currentSiteId = id;

    this.refreshPhoneBook(this._phoneBookEntries);
    this.refreshAll();
  }

  private mapPhoneNumbers(
    queues: TypeaheadServiceInput[],
    prefix: string,
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL,
    allowServiceNumber: boolean = false
  ): TypeaheadEntry<any, TargetMetaData>[] {
    const entries: TypeaheadEntry<any, TargetMetaData>[] = [];

    // the transpiled version of "for ... of" of can throw an error if the iterable is null or undefined
    if (queues !== undefined) {
      for (let queue of queues) {
        const name = queue.name;

        if (queue.phoneNumbers !== undefined) {
          let phoneNumbers: PhoneNumber[] = [];

          switch (show) {
            case TypeaheadBasketShowServicesEnum.ALL:
              phoneNumbers = queue.phoneNumbers;
              break;
            case TypeaheadBasketShowServicesEnum.INTERNAL:
              phoneNumbers = queue.phoneNumbers.filter((pn) => pn.type === PhoneNumberType.INTERNAL);
              break;
            case TypeaheadBasketShowServicesEnum.EXTERNAL:
              phoneNumbers = queue.phoneNumbers.filter((pn) => pn.type !== PhoneNumberType.INTERNAL);
              break;
            default:
              throw Error(`Unknown TypeaheadBasketShowServicesEnum=${show}`);
          }

          for (let phoneNumber of phoneNumbers) {
            let key;
            let type: string | null = null;

            const showExternal =
              show === TypeaheadBasketShowServicesEnum.EXTERNAL ||
              show === TypeaheadBasketShowServicesEnum.ALL;

            const showInternal =
              show === TypeaheadBasketShowServicesEnum.INTERNAL ||
              show === TypeaheadBasketShowServicesEnum.ALL;

            if (showExternal && this.checkExternalNumber(phoneNumber)) {
              key = this.phoneNumberPipe.transform(phoneNumber, 'compact');
              type = this.translations.external;
            } else if (showInternal && this.checkInternalNumber(phoneNumber, allowServiceNumber)) {
              key = phoneNumber.internal;
              type = this.translations.internal;
            }

            const keyE164 = this.phoneNumberPipe.transform(phoneNumber, 'clean');

            if (key !== undefined) {
              entries.push({
                group: this.translations.services,
                key: `${prefix}${name} ${key} ${keyE164}`.toLowerCase(),
                label: keyE164,
                meta: {
                  description: key,
                  group: 'services',
                  name: `${prefix}${name}`,
                  type,
                },
                value: keyE164,
              });
            }
          }
        }
      }
    }

    return entries;
  }

  // to use on language change in order to update translations of entries
  private _refreshOnLangChange: () => void = () => {};

  public refresh(
    acdQueues: Queue[] | TypeaheadServiceInput[],
    announcements: Announcement[],
    callCenter: CallCenter | Queue[] | TypeaheadServiceInput[],
    features: Features,
    ivrAssistants: IvrAssistant[] | TypeaheadServiceInput[],
    phonebook: PhoneBookContact[],
    serviceUsers: ServiceUser[] = []
  ) {
    this.logger.info('New values arrived, refreshing typeahead basket');

    const refresh = () => {
      this.refreshServices(acdQueues, callCenter, ivrAssistants, serviceUsers);
      this.refreshAnnouncements(announcements);
      this.refreshPersonalEntries(features);
      this.refreshPhoneBook(phonebook);

      this.refreshAll();
    };

    this._refreshOnLangChange = refresh;

    if (this.translations !== undefined) {
      refresh();
    } else {
      const subscription = this.translations$.subscribe(() => {
        refresh();

        subscription.unsubscribe();
      });
    }

    return this;
  }

  public refreshAcdQueues(
    queues: TypeaheadServiceInput[],
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL
  ) {
    const entries = this.mapPhoneNumbers(queues, this.translations.prefixAcd, show);

    this.services.push(...entries);

    return this;
  }

  public refreshAll() {
    let custom: TypeaheadEntry<any, TargetMetaData>[] = [];

    if (this.customOrder != null) {
      for (const key of this.customOrder) {
        if (key in this.custom) {
          custom.push(...this.custom[key]);
        } else {
          this.logger.warn('Key in custom order does not exist in custom map', key, this.custom);
        }
      }
    } else {
      custom = Object.values(this.custom).reduce((acc, value) => acc.concat(value), []);
    }

    this._all = [].concat(this.personal, custom, this.announcements, this.services, this.phoneBook);

    return this;
  }

  /**
   * Map announcements to typeahead entries
   * @param announcements
   */
  public refreshAnnouncements(announcements: Announcement[]) {
    this.announcements = announcements
      .map(
        (announcement: Announcement) =>
          ({
            key: `${announcement.description} (${announcement.waveFileName})`.toLowerCase(),
            group: this.translations.announcements,
            label: announcement.description,
            meta: {
              group: 'announcements',
              name: this.translations.announcement,
              optionName: announcement.description,
              optionType: announcement.waveFileName,
              type: announcement.waveFileName,
            },
            value: announcement.waveFileName,
          }) as TypeaheadEntry<any, TargetMetaData>
      )
      .sort(sortBy('label'));

    return this;
  }

  public refreshCallCenter(
    callCenterOrQueues: CallCenter | TypeaheadServiceInput[],
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL
  ) {
    if (Array.isArray(callCenterOrQueues)) {
      this.services.push(...this.mapPhoneNumbers(callCenterOrQueues, this.translations.prefixCc, show));
    } else {
      this.services.push(
        ...this.mapPhoneNumbers(callCenterOrQueues.queues, this.translations.prefixCc, show)
      );
    }

    return this;
  }

  public refreshCustom(customMap: StringMap<TypeaheadEntry<any, TargetMetaData>[]>) {
    this.custom = customMap;

    return this;
  }

  private checkExternalNumber(phoneNumber: PhoneNumber): boolean {
    const isExternal = phoneNumber.type !== PhoneNumberType.INTERNAL;
    const notIncoming = phoneNumber.dir !== PhoneNumberDirection.IN;
    const isNotDummyMSISDN = phoneNumber.e164?.indexOf('436760') !== 0;

    return isExternal && notIncoming && isNotDummyMSISDN;
  }

  private checkInternalNumber(phoneNumber: PhoneNumber, allowServiceNumber: boolean): boolean {
    const isInternal = phoneNumber.type === PhoneNumberType.INTERNAL;
    const notIncoming = phoneNumber.dir !== PhoneNumberDirection.IN;
    const unknownServiceType =
      allowServiceNumber || phoneNumber.serviceType === 'UNKNOWN' || phoneNumber.serviceType == null;
    const isSite = phoneNumber.siteId === this.currentSiteId;

    return isInternal && unknownServiceType && notIncoming && isSite;
  }

  /**
   * Check if a fallback number is defined and if so then create the fallback typeahead entry
   */
  private refreshFallbackNumber(endUser: EndUserData, enterprise?: Enterprise) {
    const domain = endUser.domain;
    const fallbackNumber = domain.fallbackNumberNotReachable || enterprise?.c5?.fallbackNumberNotReachable;

    if (fallbackNumber !== undefined && enterprise?.c5?.type !== DomainType.RESIDENTIAL) {
      const displayValue = fallbackNumber.replace(/@.*$/, '');
      const label = `${this.translations.fallback}: ${displayValue}`;

      this.personal.push({
        key: label.toLowerCase(),
        label: this.translations.fallback,
        meta: {
          group: 'personal',
          name: `${this.translations.extension}: ${displayValue}`,
          optionName: this.translations.fallback,
          optionType: `${this.translations.extension}: ${displayValue}`,
        },
        value: 'fallback',
      });
    }

    return this;
  }

  public refreshIvrAssistants(
    ivrAssistants: TypeaheadServiceInput[],
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL
  ) {
    this.services.push(...this.mapPhoneNumbers(ivrAssistants, this.translations.prefixIvr, show));

    return this;
  }

  /**
   * Check if a mobile device is available as a destination and if so then create the mobile typeahead entry
   */
  private refreshMobile(endUser: EndUserData, enterprise?: Enterprise) {
    let domain: Domain | C5Enterprise;
    let mobileActive: boolean;

    if (this.authenticationService.userIsEndUser()) {
      domain = endUser.domain;
    } else {
      domain = enterprise.c5;
    }

    // is the domain "Unified Communication"
    const isUC = domain.ucGateway > 0;

    // In case of UC, call forwarding to mobile makes little sense
    // Only keep if already configured in any feature as destinationUri for compatibility
    mobileActive =
      endUser.user.features.find(
        (f) =>
          f.featName.startsWith('CALL_FORWARD') &&
          Object.entries(f).find((k) => k[0].endsWith('Data') && k[1].destinationUri?.startsWith('mobile')) !=
            undefined
      ) != undefined;

    if (!mobileActive && !isUC && endUser.devices != undefined) {
      // Check if the end-user has MSISDN device
      const hasMSISDN = endUser.devices.find((device) => device.name.startsWith('Mobile: ')) != undefined;

      // If the end-user has no MSISDN device, but has a mobile phone number set in "personalization" section then mobile as destination is also valid.
      const hasMobilePhoneNumber =
        endUser.user.mobilePhone != undefined && endUser.user.mobilePhone.length > 0;

      mobileActive = hasMSISDN || hasMobilePhoneNumber;
    }

    if (mobileActive) {
      const tae: TypeaheadEntry = {
        key: this.translations.mobile.toLowerCase(),
        label: this.translations.mobile,
        meta: {
          group: 'personal',
          optionName: this.translations.mobile,
        },
        value: 'mobile',
      };
      this.personal.push(tae);
      this.parallelRingingSpecific.push(tae);
    }

    return this;
  }

  /**
   * Map features to typeahead entries
   */
  public refreshPersonalEntries(features: Features) {
    this.personal = [];
    this.parallelRingingSpecific = [];

    combineLatest([this.endUserContextService.endUser.data$, this.endUserContextService.enterprise$])
      .pipe(first())
      .subscribe(([endUser, enterprise]) => {
        this.refreshMobile(endUser, enterprise);
        this.refreshFallbackNumber(endUser, enterprise);
        this.refreshPresenceAndVoiceMail(features);
      });

    return this;
  }

  /**
   * Map phonebook entries to typeahead entries
   * @param phoneBook
   */
  public refreshPhoneBook(phoneBook: PhoneBookContact[]) {
    this._phoneBookEntries = phoneBook;

    this.phoneBook = phoneBook
      .map((phoneBook: PhoneBookContact) => this.mapPhoneBook(phoneBook))
      .reduce((a: TypeaheadEntry[], b: TypeaheadEntry[]) => a.concat(b), [])
      // label contains the editable value and not the value that is shown in the options list.
      // That's why we sort by key instead of by label.
      .sort(sortBy('key'));

    return this;
  }

  public refreshServices(
    acdQueues: TypeaheadServiceInput[],
    callCenterOrQueues: CallCenter | TypeaheadServiceInput[],
    ivrAssistants: TypeaheadServiceInput[],
    serviceUsers: ServiceUser[] = [],
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL
  ) {
    this.services = [];

    this.refreshAcdQueues(acdQueues, show);
    this.refreshCallCenter(callCenterOrQueues, show);
    this.refreshIvrAssistants(ivrAssistants, show);
    this.refreshServiceUsers(serviceUsers, show);

    // label contains the editable value and not the value that is shown in the options list.
    // That's why we sort by key instead of by label.
    this.services.sort(sortBy('key'));

    return this;
  }

  public refreshServiceUsers(
    serviceUsers: ServiceUser[],
    show: TypeaheadBasketShowServicesEnum = TypeaheadBasketShowServicesEnum.ALL
  ) {
    this.services.push(
      ...this.mapPhoneNumbers(
        serviceUsers.map((user) => {
          return {
            name: this.translateService.instant(`role.${user.role}`),
            phoneNumbers: user.phoneNumbers ?? [],
          };
        }),
        '',
        show,
        true
      )
    );

    return this;
  }

  /**
   * Check if the voicemail feature is enabled and if so then create the voicemail typeahead entry
   * @param features
   */
  private refreshPresenceAndVoiceMail(features: Features) {
    const voiceMailActive = features.voiceMail?.status === FeatureStatus.ACTIVE;

    if (voiceMailActive) {
      this.personal.push({
        key: this.translations.voicemail.toLowerCase(),
        label: this.translations.voicemail,
        meta: {
          group: 'personal',
          optionName: this.translations.voicemail,
        },
        value: 'voicemail',
      });
    }
  }
}

class TranslatedLabels {
  announcement: string;
  announcements: string;
  businessFax: string;
  businessPhone2: string;
  businessPhone: string;
  contact: string;
  endUser: string;
  external: string;
  extension: string;
  fallback: string;
  fax: string;
  homeFax: string;
  homePhone2: string;
  homePhone: string;
  internal: string;
  mobile: string;
  mobilePhone2: string;
  mobilePhone: string;
  services: string;
  phoneBook: string;
  prefixAcd: string;
  prefixCc: string;
  prefixIvr: string;
  prefixServiceUser: string;
  richPresence: string;
  richPresenceAndVoiceMail: string;
  voicemail: string;
}

export type TypeaheadEntry<T = any, Meta = StringMap<any>> = {
  disabled?: boolean;
  /** Grouping key, aka category */
  group?: string;
  /**
   * Filtering key.
   * If not set directly then consider to use the label value downcased in the component that operates on entries.
   */
  key?: string;
  /** The value visible to the user */
  label: string;
  /** any additional data we might want to add to the entry */
  meta?: Meta;
  /** data value */
  value: T;
};

export function isTypeaheadEntry<T>(value: TypeaheadEntry<T> | any): value is TypeaheadEntry<T> {
  return value != null && typeof value === 'object' && 'label' in value && 'value' in value;
}

export interface TargetMetaData {
  description?: string;
  editable?: boolean;
  group?: string;
  infoBadge?: string;
  name?: string;
  optionName?: string;
  optionType?: string;
  type?;
}

export interface TypeaheadServiceInput {
  name: string;
  phoneNumbers?: PhoneNumberLike[];
}
